"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Fragment } from "react";

import { cn } from "@/lib/utils";

import { AccountMenu } from "@/components/shell/account-menu";
import { AccountNavMenu } from "@/components/shell/account-nav-menu";
import { MobileNav } from "@/components/shell/mobile-nav";
import { primaryNav } from "@/components/shell/nav";

/**
 * Top navigation bar — a confident "product bar" for the bold redesign. The
 * primary links now live inside a defined segmented track (bordered elevated
 * pill), mirroring the right-hand utility cluster so the bar reads as two
 * symmetric rounded clusters (Railway / Runpod / Polymarket nav). The active
 * page lifts onto a `card` chip — the same segmented-control language the app
 * uses for every page-level tab (`segmented-tabs.ts`) — so the current section
 * is unmistakable instead of a faint tint. The nav order mirrors the legacy top
 * navbar — Devices, Jobs, Docs, the "My account" dropdown, then Marketplace —
 * and the right-hand cluster holds the plain user bar (avatar, username,
 * balance, logout).
 */
export function SiteHeader() {
  const pathname = usePathname();

  const isActive = (href: string) =>
    pathname === href || pathname.startsWith(`${href}/`);

  return (
    <header className="sticky top-0 z-40 w-full border-b border-hairline bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background">
      <div className="container flex h-16 items-center gap-3">
        <Link
          href="/"
          className="flex items-center gap-2.5 rounded-md font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <span
            aria-hidden="true"
            className="grid size-8 place-items-center rounded-lg bg-primary font-mono text-base font-bold text-primary-foreground shadow-sm"
          >
            X
          </span>
          <span className="hidden text-base tracking-tight sm:inline">
            Xgodo
          </span>
        </Link>

        <nav
          aria-label="Primary"
          className="mx-1 hidden items-center gap-1 rounded-full border border-hairline bg-elevated p-1 md:flex"
        >
          {primaryNav.map((item) => {
            const active = isActive(item.href);
            return (
              <Fragment key={item.href}>
                <Link
                  href={item.href}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "inline-flex h-8 items-center rounded-full px-3.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    active
                      ? // ring-primary/25 was 1.44:1 and bg-card is 1.06:1 against
                        // the header, so nothing identified the active pill.
                        // Solid --nav-active ring: 4.93:1.
                        "bg-card text-foreground shadow-sm ring-1 ring-nav-active"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {item.label}
                </Link>
                {/* Legacy order: the "My account" dropdown sits between Docs
                    and Marketplace. */}
                {item.href === "/docs" && <AccountNavMenu />}
              </Fragment>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
          <AccountMenu />
          {/* Below `md` this is the ONLY route between sections — the nav
           * track above (and the "My account" dropdown nested in it) is
           * `hidden md:flex`. Sits last so it lands under the thumb, beside
           * the logout control. */}
          <MobileNav />
        </div>
      </div>
    </header>
  );
}
